import { login } from "api/auth/login";
import { signup } from "api/auth/signup";
import { updateUser } from "api/user/updateUser";
import { uploadAvatar } from "api/user/uploadAvatar";
import { verifyAccount } from "api/user/verifyAccount";
import useLocalStorage from "hooks/useLocalStorage";
import { createContext, useContext, useCallback, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import ICustomer from "types/ICustomer";
import { checkExist } from "api/auth/checkExist";
import axios from "axios";

interface Props {
  children: React.ReactNode;
}

interface IDefaultState {
  token: string | any;
  isWebView: boolean;
  user: ICustomer | any;
  isLoadingUser: boolean;
  userError: any;
  logout: () => void;
  login: any;
  signup: any;
  updateUser: any;
  uploadAvatar: any;
  verifyAccount: any;
  checkExist: any;
  resetSignupError: () => void;
  resetCheckExist: () => void;
  setToken: (token: string | any) => void;
  setUser: (user: ICustomer | any) => void;
  setEmailVerified: () => void;
  setPhoneVerified: () => void;
  setIsWebView: (value: boolean) => void;
  fetchUserData: () => Promise<any>;
  refetchUserData: () => Promise<any>;
  lastFetchTime: number | null;
}

const initialValue = {
  token: "",
  isWebView: false,
  isLoadingUser: false,
  userError: null,
  user: {
    id: "",
    first_name: "",
    last_name: "",
    contact: {
      callingCode: "",
      phone: "",
      email: "",
    },
    image: "",
    callingCode: "",
    phone: "",
    street: "",
    province: "",
    nationality: "",
    country: "",
    city: "",
    gender: "",
    password: "",
    email: "",
    verification: {
      is_email_verified: false || true,
      is_phone_verified: false || true,
    },
  },
  login: () => {},
  logout: () => {},
  signup: () => {},
  updateUser: () => {},
  uploadAvatar: () => {},
  verifyAccount: () => {},
  checkExist: () => {},
  resetSignupError: () => {},
  resetCheckExist: () => {},
  setToken: () => {},
  setUser: () => {},
  setEmailVerified: () => {},
  setPhoneVerified: () => {},
  setIsWebView: () => {},
  fetchUserData: async () => null,
  refetchUserData: async () => null,
  lastFetchTime: null,
};

const UserContext = createContext<IDefaultState>(initialValue);

const UserProvider: React.FC<Props> = ({ children }) => {
  // Add SSR detection
  const isSSR = typeof window === "undefined";

  const [token, setToken] = useLocalStorage<any>("pass_ticket_token", null);
  const [user, setUser] = useLocalStorage<any>("pass_ticket_user", null);
  const [isWebView, setIsWebView] = useLocalStorage<boolean>(
    "pass_ticket_is_web_view",
    false
  );
  const [lastFetchTime, setLastFetchTime] = useLocalStorage<number | null>(
    "pass_ticket_user_fetch_time",
    null
  );
  const queryClient = useQueryClient();

  const loginMutate = useMutation({
    mutationFn: login,
    onSuccess: (successData: any) => {
      if (successData.errors) {
        return successData.errors[0].split(`"`)[1];
      }

      if (successData.message === "signinSuccessful") {
        setToken(successData.payload.token);
        setUser(successData.payload.user);
      }
    },
    onError: (error: any) => {
      return error;
    },
  });

  const loginCheckExist = useMutation({
    mutationFn: checkExist,
    onSuccess: (successData: any) => {
      return successData;
    },
    onError: (error: any) => {
      return error;
    },
  });

  const signupMutate = useMutation({
    mutationFn: signup,
    onSuccess: (data) => {
      if (data.message === "signupSuccessful") {
        setToken(data.payload.token);
        setUser(data.payload.user);
      }
      return data;
    },
    onError: (error: any) => {
      return error;
    },
  });

  const updateUserMutate = useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      if (data?.message != "Success") {
        throw new Error(data?.message);
      }
      if (data?.payload) {
        const picked = (({
          first_name,
          last_name,
          contact,
          image,
          gender,
          verification,
        }) => ({
          first_name,
          last_name,
          contact,
          image,
          gender,
          verification,
        }))(data.payload);

        setUser((prev: any) => ({
          ...prev,
          ...picked,
        }));
      }
    },
    onError: (error: any) => {
      throw error;
    },
  });

  const uploadAvatarMutate = useMutation({
    mutationFn: uploadAvatar,
    onSuccess: (data) => {
      if (data.status !== 500) {
        if (data.message === "Success") {
          const picked = (({ image }) => ({
            image,
          }))(data.payload);

          setUser((prev: any) => ({
            ...prev,
            ...picked,
          }));
        }
      } else {
        return data;
      }
    },
  });

  const verifyAccountMutate = useMutation({
    mutationFn: verifyAccount,
    onSuccess: async (data) => {
      if (data.message === "verified") {
        const picked = (({ verification }) => ({
          verification,
        }))(data.payload);

        // Update user with verification data from the response
        setUser((prev: any) => ({
          ...prev,
          ...picked,
        }));

        try {
          // Instead of using fetchQuery directly, use the fetchUserData function
          // which already has the proper implementation for fetching user data
          await fetchUserData(true);

          // Also invalidate related queries to ensure data consistency
          queryClient.invalidateQueries({ queryKey: ["user"] });
        } catch (error) {
          console.error("UserContext: Error refreshing user data:", error);
        }
      }
      return data; // Ensure we return the data for components using this mutation
    },
    onError: (error: any) => {
      console.error("UserContext: verifyAccount error:", error);
      return error;
    },
  });

  const resetSignupError = () => {
    signupMutate.reset();
  };

  // Check if we need to refresh the data (e.g., if it's older than 30 minutes)
  const shouldRefreshData = useCallback(() => {
    // Prevent execution during SSR
    if (isSSR) return false;

    if (!lastFetchTime) return true;

    const thirtyMinutesInMs = 30 * 60 * 1000;
    return Date.now() - lastFetchTime > thirtyMinutesInMs;
  }, [lastFetchTime, isSSR]);

  // Fetch user data from the API
  const fetchUserData = useCallback(
    async (forceRefresh = false) => {
      // If no token is available, we can't fetch user data
      if (!token) {
        return null;
      }

      try {
        const { data } = await axios.get(
          `${process.env.NEXT_PUBLIC_API_URL}users`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Update user context
        setUser(data.payload);
        // Update last fetch time
        setLastFetchTime(Date.now());
        // Invalidate any related queries
        queryClient.invalidateQueries({ queryKey: ["user"] });

        return data.payload;
      } catch (error) {
        console.error("Error fetching user data:", error);
        return null;
      }
    },
    [token, setUser, setLastFetchTime, queryClient]
  );

  // Use React Query for better caching and state management
  const {
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUserData,
  } = useQuery({
    queryKey: ["userData", token],
    queryFn: () => fetchUserData(shouldRefreshData()),
    enabled: !!token,
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
  });

  // Effect to fetch user data when token changes or data is stale
  useEffect(() => {
    if (token && (!user || shouldRefreshData())) {
      refetchUserData();
    }
  }, [token, user, shouldRefreshData, refetchUserData]);

  const logout = () => {
    // Prevent execution during SSR
    if (isSSR) return;

    // Use window check for localStorage and location access
    if (typeof window !== "undefined") {
      localStorage.removeItem("pass_ticket_token");
      localStorage.removeItem("pass_ticket_user");
      localStorage.removeItem("pass_ticket_is_web_view");
      localStorage.removeItem("pass_ticket_user_fetch_time");
      location.href = "/";
    }

    // These state updates are safe in both environments
    setToken(null);
    setUser(null);
    setLastFetchTime(null);
    queryClient.removeQueries({ queryKey: ["userData"] });
    queryClient.removeQueries({ queryKey: ["user"] });
  };

  const resetCheckExist = () => {
    loginCheckExist.reset();
  };

  const setEmailVerified = () => {
    setUser((prev: any) => ({
      ...prev,
      verification: {
        ...prev.verification,
        is_email_verified: true,
      },
    }));
  };

  const setPhoneVerified = () => {
    setUser((prev: any) => ({
      ...prev,
      verification: {
        ...prev.verification,
        is_phone_verified: true,
      },
    }));
  };

  return (
    <UserContext.Provider
      value={{
        user: user,
        token: token,
        isWebView: isWebView,
        isLoadingUser,
        userError,
        logout: logout,
        signup: signupMutate,
        login: loginMutate,
        updateUser: updateUserMutate,
        uploadAvatar: uploadAvatarMutate,
        verifyAccount: verifyAccountMutate,
        checkExist: loginCheckExist,
        resetSignupError,
        resetCheckExist,
        setToken,
        setUser,
        setEmailVerified,
        setPhoneVerified,
        setIsWebView,
        fetchUserData: () => fetchUserData(true),
        refetchUserData,
        lastFetchTime,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  return useContext(UserContext);
};

export { UserContext, UserProvider };
